/**
 * 文件库API工厂函数
 */

import type {
  FileFolder,
  FolderTreeNode,
  CreateFolderParams,
  UpdateFolderParams,
  File,
  UploadFileParams,
  UpdateFileParams,
  FileListParams,
  FileVersion,
  UploadVersionParams,
  FolderPermission,
  SetPermissionParams,
  UserFolderPermissions,
  LogListParams,
  StorageUsage,
  StorageStatItem,
  FolderDetailResponse,
} from '../../types/file';
import type { ApiResponse } from '../../types/api';
import type { IRequestClient } from '../core/client';
import { API_ROUTES } from '../routes';

function compactParams(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

function toFolderListParams(params?: {
  parentId?: number | null;
  parent_id?: number | null;
  type?: string;
}) {
  if (!params) return undefined;

  return compactParams({
    parent_id: params.parent_id ?? params.parentId,
    type: params.type,
  });
}

function toInitialPermissions(permissions?: CreateFolderParams['permissions']) {
  return permissions?.map((permission) => ({
    subject_type: (permission as any).subject_type ?? permission.subjectType,
    subject_id: (permission as any).subject_id ?? permission.subjectId,
    permission_level:
      (permission as any).permission_level ?? permission.permissionLevel,
  }));
}

function toCreateFolderPayload(data: CreateFolderParams) {
  return compactParams({
    parent_id: (data as any).parent_id ?? data.parentId,
    name: data.name,
    description: data.description,
    icon: data.icon,
    color: data.color,
    sort_order: (data as any).sort_order ?? data.sortOrder,
    type: data.type,
    permissions: toInitialPermissions(data.permissions),
  });
}

function toUpdateFolderPayload(data: UpdateFolderParams) {
  return compactParams({
    name: data.name,
    description: data.description,
    icon: data.icon,
    color: data.color,
    sort_order: (data as any).sort_order ?? data.sortOrder,
    status: data.status,
  });
}

function toPermissionPayload(data: SetPermissionParams) {
  return {
    subject_type: (data as any).subject_type ?? data.subjectType,
    subject_id: (data as any).subject_id ?? data.subjectId,
    permission_level: (data as any).permission_level ?? data.permissionLevel,
  };
}

function toFileListParams(params?: FileListParams | Record<string, any>) {
  if (!params) return undefined;

  return compactParams({
    folder_id: params.folder_id ?? params.folderId,
    page: params.page,
    per_page: params.per_page ?? params.perPage,
    search: params.search,
    extension: params.extension,
    tag: params.tag,
    created_by: params.created_by ?? params.createdBy,
    start_date: params.start_date ?? params.startDate,
    end_date: params.end_date ?? params.endDate,
    sort_field: params.sort_field ?? params.sortField,
    sort_order: params.sort_order ?? params.sortOrder,
  });
}

/**
 * 创建文件库API
 */
export function createFileApi(client: IRequestClient) {
  function buildVersionFormData(data: UploadVersionParams) {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.changeLog) formData.append('change_log', data.changeLog);
    return formData;
  }

  return {
    // ==================== 文件夹管理 ====================
    folder: {
      /** 获取文件夹列表 */
      list(params?: {
        parentId?: number | null;
        parent_id?: number | null;
        type?: string;
      }) {
        return client.get<ApiResponse<FileFolder[]>>(API_ROUTES.file.folders, {
          params: toFolderListParams(params),
        });
      },
      
      /** 获取文件夹树形结构 */
      tree() {
        return client.get<ApiResponse<FolderTreeNode[]>>(API_ROUTES.file.tree);
      },
      
      /** 获取文件夹详情 */
      detail(id: number) {
        return client.get<ApiResponse<FolderDetailResponse>>(API_ROUTES.file.detail(id));
      },

      /** 获取文件夹详情 */
      get(id: number) {
        return client.get<ApiResponse<FolderDetailResponse>>(API_ROUTES.file.detail(id));
      },
      
      /** 创建文件夹 */
      create(data: CreateFolderParams) {
        return client.post<ApiResponse<FileFolder>>(
          API_ROUTES.file.folders,
          toCreateFolderPayload(data),
        );
      },
      
      /** 更新文件夹 */
      update(id: number, data: UpdateFolderParams) {
        return client.put<ApiResponse<FileFolder>>(
          API_ROUTES.file.fileFolders(id),
          toUpdateFolderPayload(data),
        );
      },
      
      /** 删除文件夹 */
      delete(id: number, force?: boolean) {
        return client.delete<ApiResponse<boolean>>(API_ROUTES.file.delete(id), {
          params: force ? { force: true } : undefined,
        });
      },
      
      /** 移动文件夹 */
      move(id: number, parentId: number | null) {
        return client.put<ApiResponse<boolean>>(
          API_ROUTES.file.update(id),
          { parent_id: parentId },
        );
      },
      
      /** 设置文件夹权限 */
      setPermissions(folderId: number, data: SetPermissionParams) {
        return client.post<ApiResponse<boolean>>(
          API_ROUTES.file.permissions(folderId),
          toPermissionPayload(data),
        );
      },
      
      /** 获取文件夹权限 */
      getPermissions(folderId: number) {
        return client.get<ApiResponse<FolderPermission[]>>(
          API_ROUTES.file.permissions(folderId),
        );
      },
    },
    
    // ==================== 文件管理 ====================
    file: {
      
      /** 获取文件列表 */
      list(params?: FileListParams) {
        return client.get<ApiResponse<any>>(API_ROUTES.file.files, { params: toFileListParams(params) });
      },
      
      /** 获取文件详情 */
      detail(id: number) {
        return client.get<ApiResponse<File>>(API_ROUTES.file.fileFiles(id));
      },

      /** 获取文件详情 */
      get(id: number) {
        return client.get<ApiResponse<File>>(API_ROUTES.file.fileFiles(id));
      },
      
      /** 上传文件 */
      upload(data: UploadFileParams) {
        const formData = new FormData();
        formData.append('file', data.file);
        if (data.folderId !== undefined) {
          formData.append('folder_id', String(data.folderId));
        }
        if (data.name !== undefined) formData.append('name', data.name);
        if (data.description !== undefined) {
          formData.append('description', data.description);
        }
        if (data.tags !== undefined) {
          data.tags.forEach((tag, index) => formData.append(`tags[${index}]`, tag));
        }
        return client.post<ApiResponse<File>>(API_ROUTES.file.files, formData);
      },
      
      /** 更新文件 */
      update(id: number, data: UpdateFileParams) {
        return client.put<ApiResponse<File>>(API_ROUTES.file.fileFiles(id), data);
      },
      
      /** 删除文件 */
      delete(id: number) {
        return client.delete<ApiResponse<boolean>>(API_ROUTES.file.fileFiles(id));
      },
      
      /** 移动文件 */
      move(id: number, folderId: number | null) {
        return client.put<ApiResponse<boolean>>(API_ROUTES.file.move(id), { folder_id: folderId });
      },
      
      /** 复制文件 */
      copy(id: number, folderId: number | null) {
        return client.post<ApiResponse<File>>(API_ROUTES.file.copy(id), { folder_id: folderId });
      },
      
      /** 获取文件版本历史 */
      getVersions(fileId: number) {
        return client.get<ApiResponse<FileVersion[]>>(API_ROUTES.file.versions(fileId));
      },
      
      /** 新增文件版本 */
      addVersion(fileId: number, data: UploadVersionParams) {
        return client.post<ApiResponse<File>>(
          API_ROUTES.file.version(fileId),
          buildVersionFormData(data),
        );
      },

      /** 上传新版本 */
      uploadVersion(fileId: number, data: UploadVersionParams) {
        return client.post<ApiResponse<File>>(
          API_ROUTES.file.version(fileId),
          buildVersionFormData(data),
        );
      },
      
      /** 预览文件 */
      preview(fileId: number) {
        return client.get<Blob>(API_ROUTES.file.preview(fileId), {
          responseType: 'blob',
        });
      },
      
      /** 下载文件 */
      download(fileId: number) {
        return client.get<Blob>(API_ROUTES.file.download(fileId), { responseType: 'blob' });
      },
      
      /** 获取当前用户有权限的文件 */
      mine() {
        return client.get<ApiResponse<FolderTreeNode[]>>(API_ROUTES.file.tree);
      },
    },
    
    // ==================== 权限管理 ====================
    permission: {
      /** 获取文件夹权限列表 */
      list(folderId: number) {
        return client.get<ApiResponse<FolderPermission[]>>(API_ROUTES.file.permissions(folderId));
      },
      
      /** 设置文件夹权限 */
      set(folderId: number, data: SetPermissionParams) {
        return client.post<ApiResponse<FolderPermission>>(
          API_ROUTES.file.permissions(folderId),
          toPermissionPayload(data),
        );
      },
      
      /** 删除文件夹权限 */
      remove(folderId: number, subjectType: string, subjectId: number) {
        return client.delete<ApiResponse<boolean>>(API_ROUTES.file.permissions(folderId), {
          params: { subject_type: subjectType, subject_id: subjectId },
        });
      },
      
      /** 获取当前用户对文件夹的权限 */
      mine(_folderId?: number) {
        return client.get<ApiResponse<UserFolderPermissions>>(API_ROUTES.file.permissionsMine);
      },

      /** 获取当前用户对文件夹的权限 */
      getMine(_folderId?: number) {
        return client.get<ApiResponse<UserFolderPermissions>>(API_ROUTES.file.permissionsMine);
      },
    },
    
    // ==================== 版本管理 ====================
    version: {
      /** 获取版本详情 */
      detail(id: number) {
        return client.get<ApiResponse<FileVersion>>(API_ROUTES.file.fileVersions(id));
      },

      /** 获取版本详情 */
      get(id: number) {
        return client.get<ApiResponse<FileVersion>>(API_ROUTES.file.fileVersions(id));
      },
      
      /** 获取文件的版本列表 */
      list(fileId: number) {
        return client.get<ApiResponse<FileVersion[]>>(API_ROUTES.file.versions(fileId));
      },
      
      /** 下载指定版本 */
      download(id: number) {
        return client.get<Blob>(API_ROUTES.file.versionsDownload(id), { responseType: 'blob' });
      },
      
      /** 回滚到指定版本 */
      rollback(id: number) {
        return client.post<ApiResponse<File>>(API_ROUTES.file.rollback(id));
      },
    },
    
    // ==================== 日志管理 ====================
    log: {
      /** 获取访问日志列表 */
      list(params?: LogListParams) {
        return client.get<ApiResponse<any>>(API_ROUTES.file.fileLogs, { params });
      },
    },
    
    // ==================== 存储统计 ====================
    storage: {
      /** 获取存储使用情况 */
      usage() {
        return client.get<ApiResponse<StorageUsage>>(API_ROUTES.file.storageUsage);
      },
      
      /** 获取存储统计信息 */
      stats(params?: { days?: number }) {
        return client.get<ApiResponse<StorageStatItem[]>>(API_ROUTES.file.stats, { params });
      },
    },
  };
}

export type FileApi = ReturnType<typeof createFileApi>;
